import { fetchEventSource } from '@microsoft/fetch-event-source';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useEffect } from 'react';

const VITE_GRAPH_API = import.meta.env.VITE_GRAPH_API;
// The SSE endpoint lives on the gateway, same host as /graphql. Fallback mirrors
// graphql-wrapper.tsx's DEFAULT_DEV_URI so both point at the same host.
const SSE_URL = `${String(VITE_GRAPH_API ?? 'https://internal.dev.aktify.io/graphql').replace(
  /\/graphql\/?$/,
  '',
)}/notifications`;

interface Options {
  onActivity: () => void; // fired per server 'activity' event; heartbeats ignored
  onOpenChange: (open: boolean) => void; // lets the provider toggle its poll fallback
  /** False opens no connection at all — the caller isn't showing anything the feed drives. */
  enabled: boolean;
}

export const useNotificationStream = ({ onActivity, onOpenChange, enabled }: Options): void => {
  useEffect(() => {
    if (!enabled) return;
    const ctrl = new AbortController();
    void fetchEventSource(SSE_URL, {
      signal: ctrl.signal,
      openWhenHidden: true,
      // Fetched per (re)connect — fetch-event-source re-invokes this on retry, so an
      // expired token is never reused. Same auth mechanism as graphql-wrapper.tsx's authLink.
      fetch: async (input, init) => {
        const session = await fetchAuthSession();
        const token = session.tokens?.accessToken?.toString() ?? '';
        // new Headers() rather than an object spread: the library currently hands us a plain object
        // (so a spread happens to preserve its `accept: text/event-stream`), but spreading a Headers
        // instance yields {} and would silently drop it, breaking the handshake. This costs nothing
        // and doesn't depend on that internal detail.
        const headers = new Headers(init?.headers);
        headers.set('authorization', token);
        return fetch(input, { ...init, headers });
      },
      onopen: ((response: Response) => {
        // A custom onopen REPLACES the library's default validation, so validating is on us:
        // any non-throwing response lands here (401 from an expired token, non-SSE proxy 200).
        // onOpenChange(false) keeps the poll fallback alive; throwing routes into the library's
        // retry/backoff, where a later attempt re-auths.
        // Synchronous on purpose: an async handler returns a thenable that leaks React's act()
        // scope and breaks the following test. The cast satisfies the Promise<void> signature.
        const contentType = response.headers.get('content-type');
        if (!response.ok || !contentType?.startsWith('text/event-stream')) {
          onOpenChange(false);
          throw new Error(`SSE handshake failed: ${response.status}`);
        }
        onOpenChange(true);
      }) as (response: Response) => Promise<void>,
      onmessage: (ev) => {
        if (ev.event === 'activity') onActivity();
      },
      onerror: () => {
        onOpenChange(false);
        // return undefined → fetch-event-source retries with backoff. Do NOT throw (throwing stops it).
      },
    });
    return () => ctrl.abort();
  }, [onActivity, onOpenChange, enabled]);
};
